import styled, { css } from 'styled-components';

export const LayoutWrapper = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;

  ${({ theme }) => css`
    background-color: ${theme.colors?.background || '#0D0D16'};
    color: ${theme.colors?.onSurface || '#ffffff'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};
  `}
`;

export const Sidebar = styled.nav<{ $collapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $collapsed }) => ($collapsed ? '60px' : '220px')};
  transition: width 0.2s ease;
  overflow-y: auto;
  flex-shrink: 0;

  ${({ theme }) => css`
    background-color: ${theme.colors?.surfaceContainerLow || '#1A1A2E'};
    border-right: 1px solid ${theme.colors?.outlineVariant || '#333'};
  `}

  @media (max-width: 768px) {
    width: ${({ $collapsed }) => ($collapsed ? '0px' : '220px')};
    position: absolute;
    z-index: 100;
    height: 100%;
  }
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  flex-shrink: 0;

  ${({ theme }) => css`
    padding: 0 ${theme.spacing?.level4 || '16px'};
    border-bottom: 1px solid ${theme.colors?.outlineVariant || '#333'};
  `}
`;

export const SidebarBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

export const SidebarTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

export const SidebarTitle = styled.span`
  font-size: 14px;
  font-weight: 600;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurface || '#fff'};
  `}
`;

export const SidebarSubtitle = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#aaa'};
  `}
`;

export const SidebarLogo = styled.img`
  height: 24px;
  flex-shrink: 0;
`;

export const NavItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.15s ease;

  ${({ theme, $active }) => css`
    padding: 10px 12px;
    margin: 2px 8px;
    width: calc(100% - 16px);
    background-color: ${$active
      ? theme.colors?.primaryContainer || '#413ED0'
      : 'transparent'};
    color: ${$active
      ? theme.colors?.onPrimaryContainer || '#fff'
      : theme.colors?.onSurfaceVariant || '#aaa'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      background-color: ${$active
        ? theme.colors?.primaryContainer || '#413ED0'
        : theme.colors?.surfaceContainerHighest || '#2A2A3E'};
    }
  `}
`;

export const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  gap: 2px;
`;

export const NavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 18px;
  flex-shrink: 0;
`;

export const MainContent = styled.main`
  flex: 1;
  overflow-y: auto;

  ${({ theme }) => css`
    padding: 32px 40px;

    @media (max-width: 1024px) {
      padding: ${theme.spacing?.level6 || '24px'};
    }
  `}
`;

export const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 20px 0;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurface || '#fff'};
  `}
`;

export const Card = styled.div`
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;

  ${({ theme }) => css`
    background-color: ${theme.colors?.surfaceContainer || '#1E1E32'};
    border: 1px solid ${theme.colors?.outlineVariant || '#333'};
  `}
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ theme }) => css`
    padding: 14px ${theme.spacing?.level4 || '16px'};
    border-bottom: 1px solid ${theme.colors?.outlineVariant || '#333'};
  `}
`;

export const CardBody = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing?.level4 || '16px'};
  `}
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    font-size: 13px;

    ${({ theme }) => css`
      padding: ${theme.spacing?.level3 || '12px'} ${theme.spacing?.level4 || '16px'};
      border-bottom: 1px solid ${theme.colors?.outlineVariant || '#333'};
    `}
  }

  th {
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    ${({ theme }) => css`
      color: ${theme.colors?.onSurfaceVariant || '#888'};
    `}
  }

  tr:last-child td {
    border-bottom: none;
  }

  tbody tr {
    cursor: pointer;
    transition: background-color 0.1s ease;

    ${({ theme }) => css`
      &:hover {
        background-color: ${theme.colors?.surfaceContainerHighest || '#2A2A3E'};
      }
    `}
  }
`;

export const SortableHeader = styled.th<{ $active?: boolean }>`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    ${({ theme }) => css`
      color: ${theme.colors?.onSurface || '#fff'} !important;
    `}
  }

  ${({ theme, $active }) =>
    $active &&
    css`
      color: ${theme.colors?.onSurface || '#fff'} !important;
    `}
`;

export const SortIndicator = styled.span<{ $direction: 'asc' | 'desc' }>`
  display: inline-block;
  margin-left: 4px;
  font-size: 10px;
  vertical-align: middle;

  &::after {
    content: '${({ $direction }) => ($direction === 'asc' ? '▲' : '▼')}';
  }
`;

export const SearchBar = styled.input`
  width: 100%;
  max-width: 400px;
  border: none;
  outline: none;
  font-size: 13px;

  ${({ theme }) => css`
    padding: 10px 14px;
    background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
    color: ${theme.colors?.onSurface || '#fff'};
    border-radius: ${theme.borderRadius?.medium || '8px'};

    &::placeholder {
      color: ${theme.colors?.onSurfaceVariant || '#888'};
    }
  `}
`;

export const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'error' | 'info' }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;

  ${({ theme, $variant = 'info' }) => {
    const colorMap: Record<string, string> = {
      success: theme.colors?.tertiary || '#4CAF50',
      warning: theme.colors?.caution || '#FFA726',
      error: theme.colors?.error || '#EF5350',
      info: theme.colors?.primary || '#C1C1FF',
    };
    return css`
      background-color: ${colorMap[$variant]}18;
      color: ${colorMap[$variant]};
    `;
  }}
`;

export const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ theme }) => css`
    padding: ${theme.spacing?.level3 || '12px'} ${theme.spacing?.level4 || '16px'};
  `}
`;

export const PaginationInfo = styled.span`
  font-size: 12px;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#888'};
  `}
`;

export const PaginationButton = styled.button<{ $disabled?: boolean }>`
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 6px;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};

  ${({ theme }) => css`
    background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
    color: ${theme.colors?.onSurface || '#fff'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover:not(:disabled) {
      background-color: ${theme.colors?.surfaceContainerHighest || '#2A2A3E'};
    }
  `}
`;

export const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.6);
`;

export const DialogContent = styled.div`
  width: 100%;
  max-width: 480px;
  border-radius: 16px;
  max-height: 80vh;
  overflow-y: auto;

  ${({ theme }) => css`
    background-color: ${theme.colors?.surfaceContainerLow || '#1A1A2E'};
    border: 1px solid ${theme.colors?.outlineVariant || '#333'};
    padding: ${theme.spacing?.level6 || '24px'};
  `}
`;

export const DialogTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurface || '#fff'};
  `}
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

export const FormLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#888'};
  `}
`;

export const FormInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;

  ${({ theme }) => css`
    padding: 10px ${theme.spacing?.level3 || '12px'};
    background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
    color: ${theme.colors?.onSurface || '#fff'};
    border-radius: ${theme.borderRadius?.medium || '8px'};
  `}

  &:disabled {
    opacity: 0.5;
  }
`;

export const FormSelect = styled.select`
  border: none;
  outline: none;
  font-size: 14px;
  cursor: pointer;

  ${({ theme }) => css`
    padding: 10px ${theme.spacing?.level3 || '12px'};
    background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
    color: ${theme.colors?.onSurface || '#fff'};
    border-radius: ${theme.borderRadius?.medium || '8px'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};
  `}
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 24px;
`;

export const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' }>`
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 18px;
  border-radius: 8px;
  transition: all 0.15s ease;

  ${({ theme, $variant = 'ghost' }) => {
    if ($variant === 'primary') {
      return css`
        background-color: ${theme.colors?.primary || '#C1C1FF'};
        color: ${theme.colors?.onPrimary || '#1A1A2E'};
        font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

        &:hover { opacity: 0.85; }
      `;
    }
    if ($variant === 'danger') {
      return css`
        background-color: ${theme.colors?.error || '#EF5350'};
        color: #000;
        font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

        &:hover { opacity: 0.85; }
      `;
    }
    return css`
      background-color: transparent;
      color: ${theme.colors?.onSurfaceVariant || '#aaa'};
      font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

      &:hover {
        background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
        color: ${theme.colors?.onSurface || '#fff'};
      }
    `;
  }}
`;

export const EmptyState = styled.div`
  text-align: center;
  font-size: 13px;

  ${({ theme }) => css`
    padding: ${theme.spacing?.level8 || '48px'};
    color: ${theme.colors?.onSurfaceVariant || '#888'};
  `}
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px 24px;
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const DetailLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#888'};
  `}
`;

export const DetailValue = styled.span`
  font-size: 14px;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurface || '#fff'};
  `}
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
`;

export const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;

  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.colors?.outlineVariant || '#333'};
    padding-bottom: 0;
  `}
`;

export const Tab = styled.button<{ $active?: boolean }>`
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  padding: 10px 20px;
  border-radius: 8px 8px 0 0;
  transition: all 0.15s ease;
  position: relative;

  ${({ theme, $active }) => css`
    background-color: transparent;
    color: ${$active
      ? theme.colors?.primary || '#C1C1FF'
      : theme.colors?.onSurfaceVariant || '#aaa'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      border-radius: 2px 2px 0 0;
      background-color: ${$active ? theme.colors?.primary || '#C1C1FF' : 'transparent'};
    }

    &:hover {
      color: ${theme.colors?.onSurface || '#fff'};
    }
  `}
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const SectionTitle = styled.span`
  font-weight: 600;
  font-size: 15px;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurface || '#fff'};
  `}
`;

export const CardHeaderLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

export const PaginationActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const TruncatedCell = styled.td`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SmallText = styled.span`
  font-size: 11px;
  word-break: break-all;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#aaa'};
  `}
`;

export const MutedLabel = styled.span`
  opacity: 0.5;
  margin-left: 6px;
  font-size: 11px;
`;

export const SmallCell = styled.td`
  font-size: 11px;
`;

export const PositiveAmount = styled.td`
  font-weight: 500;

  ${({ theme }) => css`
    color: ${theme.colors?.tertiary || '#4CAF50'};
  `}
`;

export const NegativeAmount = styled.td`
  font-weight: 500;

  ${({ theme }) => css`
    color: ${theme.colors?.error || '#EF5350'};
  `}
`;

export const FlexSpacer = styled.div`
  flex: 1;
`;

export const CollapseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 16px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#aaa'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover {
      background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
      color: ${theme.colors?.onSurface || '#fff'};
    }
  `}
`;

export const GhostLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  font-size: inherit;

  ${({ theme }) => css`
    color: ${theme.colors?.primary || '#C1C1FF'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};
  `}
`;

export const WarningBanner = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.82rem;
  line-height: 1.45;

  ${({ theme }) => css`
    background-color: ${theme.colors?.cautionContainer || '#2a2000'};
    color: ${theme.colors?.caution || '#FFA726'};
    border: 1px solid ${theme.colors?.caution || '#FFA726'}30;
  `}
`;

export const InfoTooltip = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: 4px;
  cursor: default;

  ${({ theme }) => css`
    color: ${theme.colors?.onSurfaceVariant || '#888'};
  `}

  &:hover > span {
    visibility: visible;
    opacity: 1;
  }
`;

export const TooltipText = styled.span`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 260px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: normal;
  line-height: 1.4;
  z-index: 100;
  pointer-events: none;
  transition: opacity 0.15s ease;
  white-space: normal;

  ${({ theme }) => css`
    background-color: ${theme.colors?.surfaceContainerHighest || '#2A2A3E'};
    color: ${theme.colors?.onSurface || '#fff'};
    border: 1px solid ${theme.colors?.outlineVariant || '#444'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  `}
`;

export const TagList = styled.td`
  & > span {
    display: inline-block;
    font-size: 0.7rem;

    ${({ theme }) => css`
      padding: ${theme.spacing?.level1 || '1px'} ${theme.spacing?.level2 || '6px'};
      margin: ${theme.spacing?.level1 || '1px'} ${theme.spacing?.level1 || '3px'}
        ${theme.spacing?.level1 || '1px'} 0;
      border-radius: ${theme.borderRadius?.small || '4px'};
      background-color: ${theme.colors?.surfaceContainerHigh || '#333'};
      color: ${theme.colors?.onSurface || '#ddd'};
    `}
  }
`;

export const ExportCsvButton = styled.button<{ $loading?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  cursor: ${({ $loading }) => ($loading ? 'wait' : 'pointer')};
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: ${({ $loading }) => ($loading ? 0.6 : 1)};
  transition: all 0.15s ease;

  ${({ theme }) => css`
    background-color: transparent;
    color: ${theme.colors?.onSurfaceVariant || '#aaa'};
    font-family: ${theme.font?.fontFamily || "'Inter Variable', 'Inter', sans-serif"};

    &:hover:not(:disabled) {
      background-color: ${theme.colors?.surfaceContainerHigh || '#252540'};
      color: ${theme.colors?.onSurface || '#fff'};
    }
  `}

  & > svg {
    flex-shrink: 0;
  }
`;

export const ToggleButton = styled.button<{ $active?: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;

  ${({ theme, $active }) => css`
    background-color: ${$active
      ? theme.colors?.primary || '#C1C1FF'
      : theme.colors?.surfaceContainerHigh || '#333'};
  `}

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $active }) => ($active ? '23px' : '3px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: white;
    transition: left 0.2s ease;
  }
`;
